import React, { useState } from 'react';
import { Instagram, Youtube, ExternalLink, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const INSTAGRAM_URL = 'https://www.instagram.com/enrigo_us';
const YOUTUBE_URL = 'https://www.youtube.com/@enrigo-nc';

const SocialMedia = () => {
  const [instaLoaded, setInstaLoaded] = useState(false);

  return (
    <div className="bg-black text-white pt-24" data-testid="social-media-page">
      {/* Hero */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F4B400]/10 via-transparent to-purple-900/10" />
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-5 py-2 mb-8">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-sm text-gray-300 tracking-wide">Stay Connected</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
              Follow Our <span className="text-[#F4B400]">Creative Journey</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-400 leading-relaxed max-w-2xl mx-auto">
              Behind-the-scenes looks, latest projects, design inspiration, and creative process.
              Join our community across platforms.
            </p>
          </div>
        </div>
      </section>

      {/* Instagram Section - Auto-Updating Embed */}
      <section className="py-20 bg-white text-black relative">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center">
                  <Instagram className="text-white" size={20} />
                </div>
                <span className="text-sm font-semibold text-gray-500 tracking-widest uppercase">Instagram</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold">@enrigo_us</h2>
              <p className="text-gray-500 mt-2 max-w-md">Our latest work, process shots, and design inspiration — updated live.</p>
            </div>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-follow-btn"
            >
              <Button className="mt-6 md:mt-0 bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 text-white hover:opacity-90 transition-opacity px-8 py-5 text-base">
                Follow on Instagram
                <ExternalLink className="ml-2" size={18} />
              </Button>
            </a>
          </div>

          {/* Instagram Embed - Auto-updating */}
          <div className="relative max-w-2xl mx-auto" data-testid="instagram-embed">
            {!instaLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-2xl z-10">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500 mx-auto mb-3"></div>
                  <p className="text-gray-400 text-sm">Loading Instagram feed...</p>
                </div>
              </div>
            )}
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
              <iframe
                src="https://www.instagram.com/enrigo_us/embed"
                className="w-full border-0"
                style={{ minHeight: '520px' }}
                scrolling="no"
                allowTransparency="true"
                loading="lazy"
                title="Instagram Feed @enrigo_us"
                onLoad={() => setInstaLoaded(true)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* YouTube Section */}
      <section className="py-20 bg-black relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-red-900/5 to-transparent" />
        <div className="container mx-auto px-6 relative z-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-red-600 flex items-center justify-center">
                  <Youtube className="text-white" size={20} />
                </div>
                <span className="text-sm font-semibold text-gray-500 tracking-widest uppercase">YouTube</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white">Enrigo Studio</h2>
              <p className="text-gray-400 mt-2 max-w-md">Video content, tutorials, and behind-the-scenes of our creative process.</p>
            </div>
            <a
              href={YOUTUBE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="youtube-subscribe-btn"
            >
              <Button className="mt-6 md:mt-0 bg-red-600 text-white hover:bg-red-700 transition-colors px-8 py-5 text-base">
                Subscribe to Channel
                <ExternalLink className="ml-2" size={18} />
              </Button>
            </a>
          </div>

          {/* YouTube Coming Soon */}
          <Card className="bg-white/5 border-white/10 overflow-hidden" data-testid="youtube-section">
            <CardContent className="p-0">
              <div className="flex flex-col lg:flex-row items-center">
                <div className="lg:w-1/2 p-10 lg:p-16">
                  <div className="inline-flex items-center gap-2 bg-red-600/20 border border-red-600/30 rounded-full px-4 py-1.5 mb-6">
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-red-400 text-sm font-medium">Coming Soon</span>
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-4">Video Content is On The Way</h3>
                  <p className="text-gray-400 leading-relaxed mb-8">
                    We're preparing exciting video content including project walkthroughs, design tutorials, 
                    creative process breakdowns, and client success stories. Subscribe now to be the first to know!
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer">
                      <Button className="bg-red-600 text-white hover:bg-red-700">
                        <Youtube className="mr-2" size={18} />
                        Subscribe Now
                      </Button>
                    </a>
                    <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                        <Instagram className="mr-2" size={18} />
                        Follow Meanwhile
                      </Button>
                    </a>
                  </div>
                </div>
                <div className="lg:w-1/2 p-8 flex items-center justify-center">
                  <div className="relative w-full max-w-md aspect-video bg-white/5 rounded-2xl border border-white/10 flex items-center justify-center group cursor-pointer">
                    <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center">
                      <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-red-600/30">
                        <Play className="text-white ml-1" size={32} fill="white" />
                      </div>
                      <span className="text-gray-500 text-sm mt-4">@enrigo-nc</span>
                    </a>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* All Social Links CTA */}
      <section className="py-20 bg-white text-black">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-4">Connect With Us Everywhere</h2>
            <p className="text-gray-500 mb-10 text-lg">Follow us on your favorite platform and stay updated with our latest work.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="social-links-grid">
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer"
                className="group p-6 rounded-2xl border-2 border-gray-200 hover:border-pink-500 transition-all duration-300 flex flex-col items-center gap-3"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Instagram className="text-white" size={24} />
                </div>
                <span className="font-semibold text-sm">Instagram</span>
              </a>
              <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer"
                className="group p-6 rounded-2xl border-2 border-gray-200 hover:border-red-500 transition-all duration-300 flex flex-col items-center gap-3"
              >
                <div className="w-14 h-14 rounded-xl bg-red-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Youtube className="text-white" size={24} />
                </div>
                <span className="font-semibold text-sm">YouTube</span>
              </a>
              <a href="https://www.facebook.com/enrigous" target="_blank" rel="noopener noreferrer"
                className="group p-6 rounded-2xl border-2 border-gray-200 hover:border-blue-500 transition-all duration-300 flex flex-col items-center gap-3"
              >
                <div className="w-14 h-14 rounded-xl bg-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <svg className="text-white" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </div>
                <span className="font-semibold text-sm">Facebook</span>
              </a>
              <a href="https://wa.me/18608470649" target="_blank" rel="noopener noreferrer"
                className="group p-6 rounded-2xl border-2 border-gray-200 hover:border-green-500 transition-all duration-300 flex flex-col items-center gap-3"
              >
                <div className="w-14 h-14 rounded-xl bg-green-500 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <svg className="text-white" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </div>
                <span className="font-semibold text-sm">WhatsApp</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SocialMedia;
